import type { ServiceLayerAuthConfig } from "./config";
import { resolveApiBase } from "./config";
import { getSupabaseClient } from "./supabaseClient";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ApiRequestInit extends RequestInit {
  method?: HttpMethod;
}

/**
 * Typed error thrown by {@link apiJson} on a non-2xx response.
 *
 * Carries the numeric HTTP `status` (and a derived envelope `code` when the
 * body is a `{error:{code}}` envelope) as STRUCTURED fields, so consumers can
 * branch on `status === 401` instead of substring-matching `error.message`.
 * The brittle `error.message.includes('UNAUTHORIZED')` check in
 * authCheckStatus.ts depended on the message text — a renamed/translated
 * message silently changed the all-dark overlay behaviour
 * (BUG-FE-SILENT-SESSION-JWT-DEGRADATION-2026-05-31). The message format is
 * preserved verbatim for backward compatibility with any existing readers.
 */
export class ServiceLayerApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly bodyText: string,
    /** Envelope `error.code` when the body was a structured error envelope. */
    public readonly code?: string,
  ) {
    super(`API request failed: ${status} ${statusText} - ${bodyText}`);
    this.name = 'ServiceLayerApiError';
  }
}

/** Narrow an unknown caught value to {@link ServiceLayerApiError}. */
export function isServiceLayerApiError(e: unknown): e is ServiceLayerApiError {
  return e instanceof ServiceLayerApiError;
}

/** Extract `error.code` from an SL `{error:{code,message}}` (or legacy
 *  `{detail:{error:{code}}}`) JSON body string, if present. */
function extractEnvelopeCode(bodyText: string): string | undefined {
  try {
    const parsed = JSON.parse(bodyText) as Record<string, unknown>;
    const direct = (parsed?.error as { code?: unknown } | undefined)?.code;
    if (typeof direct === 'string') return direct;
    const nested = (
      (parsed?.detail as { error?: { code?: unknown } } | undefined)?.error
    )?.code;
    if (typeof nested === 'string') return nested;
  } catch {
    // non-JSON body → no structured code; status alone is authoritative.
  }
  return undefined;
}

export interface SessionShape {
  user?: {
    id?: string | null;
    email?: string | null;
    role?: string | null;
    expires_at?: number | null;
  } | null;
  csrf_token?: string | null;
  request_id?: string | null;
}

function isUnsafe(method?: string): boolean {
  if (!method) return false;
  const upper = method.toUpperCase();
  return upper !== "GET" && upper !== "HEAD" && upper !== "OPTIONS";
}

export async function apiFetch(
  config: ServiceLayerAuthConfig,
  path: string,
  init: ApiRequestInit = {},
  csrfToken?: string | null,
  impersonateEmail?: string | null
): Promise<Response> {
  const base = resolveApiBase(config);
  const url = path.startsWith("http") ? path : `${base}${path}`;

  const headers = new Headers(init.headers as HeadersInit);
  if (isUnsafe(init.method) && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }
  if (impersonateEmail) {
    headers.set("X-Impersonate-User", impersonateEmail);
  }
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return fetch(url, {
    ...init,
    credentials: "include",
    headers
  });
}

export async function apiJson<T = unknown>(
  config: ServiceLayerAuthConfig,
  path: string,
  init: ApiRequestInit = {},
  csrfToken?: string | null,
  impersonateEmail?: string | null
): Promise<T> {
  const res = await apiFetch(config, path, init, csrfToken, impersonateEmail);
  if (!res.ok) {
    const text = await res.text().catch(() => "Unknown error");
    // Throw a TYPED error carrying the structured status/code so consumers
    // (e.g. authCheckStatus.ts) branch on `status === 401` rather than
    // substring-matching the message. Message text is identical to the prior
    // `Error` for backward compatibility.
    throw new ServiceLayerApiError(res.status, res.statusText, text, extractEnvelopeCode(text));
  }
  return (await res.json()) as T;
}

export async function getSession(
  config: ServiceLayerAuthConfig
): Promise<SessionShape> {
  return apiJson<SessionShape>(config, "/v1/auth/session", {
    method: "GET",
    headers: { "Content-Type": "application/json" }
  });
}

export async function logout(config: ServiceLayerAuthConfig): Promise<void> {
  // Tear down the AUTHORITATIVE Supabase session created by this package FIRST
  // (handleAuthCallback -> setSession persists it at localStorage['sb-clanda-sl']
  // via getSupabaseClient(config), persistSession:true). Since PR #1770 this
  // session is the source of truth that the SPA replays into
  // /v1/auth/cookie-handoff on the next boot, so clearing only the cookie
  // leaves a live session that silently re-authenticates the previous user.
  // signOut({ scope: 'local' }) clears both the in-memory session and the
  // sb-clanda-sl key without a network round-trip. It runs before the cookie
  // POST so a network failure on /v1/auth/logout cannot leave the local
  // session live; failures here are swallowed so the cookie clear still runs.
  try {
    await getSupabaseClient(config).auth.signOut({ scope: "local" });
  } catch {
    // Local sign-out is best-effort; do not block the cookie clear below.
  }

  // Clear the SL HttpOnly cookie server-side.
  await apiFetch(
    config,
    "/v1/auth/logout",
    { method: "POST" },
    undefined
  );
}

/**
 * Create an API fetch function with impersonation support.
 * 
 * Note: Service Layer enforces that only superadmin and sales_manager roles
 * can impersonate. This function simply adds the header; server-side validation
 * will reject unauthorized impersonation attempts.
 * 
 * @param impersonateEmail - Email of the user to impersonate
 * @returns A function that can be used like apiFetch but with impersonation header
 */
export function withImpersonation(
  impersonateEmail: string
): (
  config: ServiceLayerAuthConfig,
  path: string,
  init?: ApiRequestInit,
  csrfToken?: string | null
) => Promise<Response> {
  return (config, path, init = {}, csrfToken) =>
    apiFetch(config, path, init, csrfToken, impersonateEmail);
}



